import type { AutoAllocationPlan } from '@obraplan/core/domain/planning/auto-allocation';
import { compareNames } from '@obraplan/core/domain/shared/naming';

/**
 * A proposta automática arrumada para a tela (Fase 30, etapa 4 — RN-432).
 *
 * Na obra por pessoa, uma linha por funcionário, como sempre. Na obra por equipe, as linhas que
 * levam equipe se juntam nela — o nome, as pessoas, as horas somadas e as frentes —, e quem não
 * tem equipe vem por pessoa. Nada é recalculado aqui: as horas são as da proposta, só somadas.
 */

export interface ProposalActivity {
  /** A chave da linha na tela: frente × serviço — a frente pode ter dois serviços (RN-434). */
  readonly key: string;
  readonly activityId: string;
  readonly activityLabel: string;
  /** `"ALVENARIA NÃO ESTRUTURAL"` — a tela mostra frente · serviço. */
  readonly serviceName?: string;
  readonly quarterHours: number;
  /** As horas do dia — somadas entre os membros, na linha da equipe. */
  readonly byDate: ReadonlyMap<string, number>;
}

export interface ProposalPerson {
  readonly resourceId: string;
  readonly resourceName: string;
  readonly quarterHours: number;
  readonly activities: readonly ProposalActivity[];
}

export interface ProposalTeam {
  readonly teamId: string;
  readonly teamName: string;
  readonly quarterHours: number;
  /** Quem recebe pela equipe, com as horas de cada um nela. */
  readonly members: readonly ProposalPerson[];
  /** As frentes da equipe, com as horas dos membros somadas por dia. */
  readonly activities: readonly ProposalActivity[];
}

export interface ProposalGroups {
  readonly teams: readonly ProposalTeam[];
  /** Por pessoa: quem não tem equipe e o terceirizado (D4) — e todo mundo na obra por pessoa. */
  readonly people: readonly ProposalPerson[];
}

interface Acumulada {
  readonly name: string;
  readonly activities: ProposalActivity[];
}

interface EquipeAcumulada {
  readonly name: string;
  readonly members: Map<string, Acumulada>;
  readonly activities: Map<
    string,
    {
      readonly activityId: string;
      readonly label: string;
      readonly serviceName?: string;
      readonly byDate: Map<string, number>;
    }
  >;
}

export function groupProposal(
  proposal: AutoAllocationPlan | undefined,
  byTeam: boolean,
): ProposalGroups {
  if (proposal === undefined) return { teams: [], people: [] };

  const people = new Map<string, Acumulada>();
  const teams = new Map<string, EquipeAcumulada>();

  for (const line of proposal.lines) {
    const key = `${line.activityId}|${line.serviceId ?? ''}`;
    const servico = line.serviceName === undefined ? {} : { serviceName: line.serviceName };
    const activity: ProposalActivity = {
      key,
      activityId: line.activityId,
      activityLabel: line.activityLabel,
      ...servico,
      quarterHours: line.quarterHours,
      byDate: new Map(line.days.map((day) => [day.date, day.quarterHours])),
    };

    if (!byTeam || line.plannedTeamId === undefined) {
      const person = people.get(line.resourceId) ?? { name: line.resourceName, activities: [] };
      person.activities.push(activity);
      people.set(line.resourceId, person);
      continue;
    }

    const team: EquipeAcumulada = teams.get(line.plannedTeamId) ?? {
      name: line.teamName ?? 'Equipe',
      members: new Map(),
      activities: new Map(),
    };
    const member: Acumulada = team.members.get(line.resourceId) ?? {
      name: line.resourceName,
      activities: [],
    };
    member.activities.push(activity);
    team.members.set(line.resourceId, member);

    const front = team.activities.get(key) ?? {
      activityId: line.activityId,
      label: line.activityLabel,
      ...servico,
      byDate: new Map<string, number>(),
    };
    for (const day of line.days) {
      front.byDate.set(day.date, (front.byDate.get(day.date) ?? 0) + day.quarterHours);
    }
    team.activities.set(key, front);
    teams.set(line.plannedTeamId, team);
  }

  const toPerson = (
    resourceId: string,
    person: { name: string; activities: readonly ProposalActivity[] },
  ): ProposalPerson => ({
    resourceId,
    resourceName: person.name,
    activities: person.activities,
    quarterHours: sumOf(person.activities),
  });

  return {
    teams: [...teams.entries()]
      .map(([teamId, team]): ProposalTeam => {
        const activities = [...team.activities.entries()].map(([key, front]) => ({
          key,
          activityId: front.activityId,
          activityLabel: front.label,
          ...(front.serviceName === undefined ? {} : { serviceName: front.serviceName }),
          byDate: front.byDate,
          quarterHours: [...front.byDate.values()].reduce((sum, value) => sum + value, 0),
        }));
        return {
          teamId,
          teamName: team.name,
          members: [...team.members.entries()].map(([resourceId, member]) =>
            toPerson(resourceId, member),
          ),
          activities,
          quarterHours: sumOf(activities),
        };
      })
      .sort((a, b) => compareNames(a.teamName, b.teamName)),
    people: [...people.entries()].map(([resourceId, person]) => toPerson(resourceId, person)),
  };
}

function sumOf(activities: readonly { readonly quarterHours: number }[]): number {
  return activities.reduce((sum, activity) => sum + activity.quarterHours, 0);
}

export function countLabel(count: number, singular: string, plural: string): string {
  return `${String(count)} ${count === 1 ? singular : plural}`;
}

/**
 * "2 equipes e 3 pessoas" — a parte zerada sai: "2 equipes", "1 pessoa". Sem nenhuma das duas,
 * "nenhuma pessoa", para a frase não ficar sem sujeito.
 */
export function teamsAndPeopleLabel(teams: number, people: number): string {
  const partes = [
    ...(teams > 0 ? [countLabel(teams, 'equipe', 'equipes')] : []),
    ...(people > 0 ? [countLabel(people, 'pessoa', 'pessoas')] : []),
  ];
  return partes.length === 0 ? 'nenhuma pessoa' : partes.join(' e ');
}
